import { useVirtualizer, Virtualizer } from '@tanstack/react-virtual';
import { memo, RefObject, useCallback, useEffect, useMemo, useState } from 'react';
import React from 'react';
import { Document } from 'react-pdf';

import { Flex, Save } from '@/components/core';
import { useStabilizedBlob } from '@/hooks';
import { useDocumentStore, useValidationStore } from '@/store';
import { DivProps } from '@/utils';

import { DocumentPage } from './DocumentPage';
import { ImageViewer } from './ImageViewer';

export type DocumentViewerProps = Omit<DivProps, 'children'> & {
  containerRef: React.RefObject<HTMLDivElement>;
};

const IDocumentViewer = ({ containerRef }: DocumentViewerProps) => {
  const documentBlob = useDocumentStore((state) => state.documentBlob);
  const numPages = useDocumentStore((state) => state.numPages);
  const rotation = useDocumentStore((state) => state.rotation);
  const setCurrentPages = useDocumentStore((state) => state.setCurrentPages);
  const setNumPages = useDocumentStore((state) => state.setNumPages);
  const zoom = useDocumentStore((state) => state.zoom);

  const onScrollToPage = useValidationStore((state) => state.onScrollToPage);
  const pageHeight = useValidationStore((state) => state.parentContainerHeight);

  const options = useMemo(() => {
    return {
      cMapUrl: '/cmaps/',
      standardFontDataUrl: '/standard_fonts/',
    };
  }, []);

  const onLoadSuccess = useCallback(
    ({ numPages }: { numPages: number }) => {
      setNumPages(numPages);
      setCurrentPages([0]);
    },
    [setCurrentPages, setNumPages]
  );

  const onLoadError = useCallback((error: Error) => {
    console.error(error);
  }, []);

  const onChange = useCallback(
    (virt: Virtualizer<HTMLDivElement, Element>, sync: boolean) => {
      if (!sync) {
        const scrollOffset = virt.scrollOffset;

        if (scrollOffset != null && containerRef.current) {
          const containerHeight = containerRef.current.getBoundingClientRect().height;

          const intersecting = virt.getVirtualItems().filter((item) => {
            const x = Math.max(item.start, scrollOffset);
            const y = Math.min(item.end, scrollOffset + containerHeight);
            const u = Math.min(item.start, scrollOffset);
            const v = Math.max(item.end, scrollOffset + containerHeight);
            const intersectionOfUnion = Math.max(y - x, 0) / (v - u);

            return intersectionOfUnion > 0.1;
          });

          const currentPages = intersecting.map((virtualPage) => virtualPage.index);
          setCurrentPages([...currentPages]);
        }
      }
    },
    [containerRef, setCurrentPages]
  );

  const virtualizer = useVirtualizer({
    count: numPages ?? 1,
    getScrollElement: () => containerRef.current,
    estimateSize: () => (pageHeight ?? 1000) * zoom,
    measureElement: () => (pageHeight ?? 1000) * zoom,
    overscan: 3,
    gap: 16,
    onChange,
  });

  const items = virtualizer.getVirtualItems();

  useEffect(() => {
    onScrollToPage?.((page: number) => {
      virtualizer.scrollToIndex(page, { align: 'center', behavior: 'smooth' });
    });
  }, [onScrollToPage, virtualizer]);

  const { blob, key: documentKey } = useStabilizedBlob(documentBlob);

  useEffect(() => {
    virtualizer.measure();
  }, [zoom, virtualizer, pageHeight]);

  return blob?.type === 'application/pdf' ? (
    <Document
      key={documentKey}
      file={blob}
      options={options}
      rotate={rotation}
      onLoadError={onLoadError}
      onLoadSuccess={onLoadSuccess}
      externalLinkTarget="_blank"
      loading={<span></span>}
    >
      <Flex col className="gap-sm relative w-full" style={{ height: virtualizer.getTotalSize() }}>
        {items.map((virtualPage) => {
          return (
            <div
              data-index={virtualPage.index}
              ref={virtualizer.measureElement}
              key={virtualPage.key}
              style={{
                position: 'absolute',
                top: 0,
                height: virtualPage.size,
                width: '100%',
                transform: `translateY(${virtualPage.start}px)`,
              }}
            >
              <DocumentPage page={virtualPage.index + 1} height={pageHeight} />
            </div>
          );
        })}
        {items.length === 0 && <DocumentPage page={0} height={pageHeight} />}
      </Flex>
    </Document>
  ) : blob && ['image/jpeg', 'image/png', 'image/webp'].includes(blob.type) ? (
    <Flex col className="h-full w-full">
      <ImageViewer />
    </Flex>
  ) : (
    <Flex className="h-full w-full justify-center">
      <Flex className="h-full w-3/5">
        <Save message="Loading document ..." />
      </Flex>
    </Flex>
  );
};

type DocumentWrapperProps = Omit<DocumentViewerProps, 'containerRef'>;

const DocumentWrapper = ({ ...rest }: DocumentWrapperProps) => {
  const [containerRef, setContainerRef] = useState<RefObject<HTMLDivElement | null> | null>(null);

  const onRefAcquired = useCallback(
    (el: HTMLDivElement | null) => {
      if (!containerRef) {
        const ref = React.createRef<HTMLDivElement>();
        ref.current = el;
        setContainerRef(ref);
      } else {
        containerRef.current = el;
      }
    },
    [containerRef]
  );

  return (
    <div ref={onRefAcquired} className="h-full w-full overflow-auto">
      {containerRef != null && <IDocumentViewer containerRef={containerRef as RefObject<HTMLDivElement>} {...rest} />}
    </div>
  );
};

export const DocumentViewer = memo(DocumentWrapper) as typeof DocumentWrapper;
